import apiService from '@/api'
import ConversationTransport from '@/services/conversation/transport/ConversationTransport'
import { runGenerationTurn } from '@/services/conversation/turn/runGenerationTurn'
import { StoryTurnError, runStoryTurn } from '@/features/story-conversation/runStoryTurn'
import { completeResponse, responseProgress, startResponse } from '@/features/story-conversation/responseState'
import type { TimelineBinding, TurnStreamContext } from '@/services/conversation/turn/types'
import type { Character, Persona, Bot } from '@/types/domain'
import { roleplayPrimaryRecipe } from '@/features/roleplay-conversation/context/roleplayRecipe'
import type { RoleplayContextInput } from '@/features/roleplay-conversation/context/roleplayRecipe'
import type { ContextRecipe } from '@/services/conversation/context/types'
import { renderConversationMacros } from '@/features/roleplay-conversation/context/promptMacros'
import { parseErrorMessage } from '@/services/errorParser'
import type { DirectorNoteFromComposer } from '@/features/roleplay-conversation/composer/chatComposerState'
import type { ContextMessage } from '@/features/roleplay-conversation/generation/RoleplayContextBuilder'
import type { StreamHandle } from '@/services/conversation/transport/ConversationTransport'
import type { MessagePhase, StoreMessage } from '@/lib/messageSlots'
import type { RoleplayConversationSessionContract } from '@/features/roleplay-conversation/session/conversationSession'
import { UserMessageSaveUncertain } from '@/features/roleplay-conversation/session/saveRecovery'

type ApiService = typeof apiService

/**
 * Confirmed user-save rejection restores the composer. Uncertain writes retain
 * their timeline entry for receipt recovery; generation never starts before a
 * confirmed user save. Assistant-save failures retain the completed prose.
 */
export type MessageSendPhase = 'user-persist' | 'user-save-uncertain' | 'stream' | 'assistant-persist'

export class MessageSendError extends Error {
  phase: MessageSendPhase
  constructor(phase: MessageSendPhase, message: string) {
    super(message)
    this.name = 'MessageSendError'
    this.phase = phase
  }
}

interface MessageUpdate {
  content?: string
  phase?: MessagePhase
  stream_id?: string
  [key: string]: unknown
}

interface RoleplayMessageOrchestratorOptions {
  conversationsStore?: RoleplayConversationSessionContract | null
  messageStore?: RoleplayConversationSessionContract | null
  onMessageUpdate?: ((messageId: number | string | null, updates: MessageUpdate & { aborted?: boolean; chunk?: string }) => void) | null
  onError?: ((error: Error) => void) | null
  api?: ApiService
  /** Swappable so a future surface can compose a roleplay turn differently. */
  recipe?: ContextRecipe<RoleplayContextInput>
  transport?: ConversationTransport
  logger?: Console
}

interface SendMessageParams {
  messageContent: string
  directorNote?: DirectorNoteFromComposer | null
  conversationId: number | string | null
  character: Character
  persona: Persona
  messages: StoreMessage[]
  currentBot: Bot
  skipUserMessage?: boolean
  options?: Record<string, unknown>
  isCancelled?: () => boolean
}

interface RerollMessageParams {
  messageId: number | string
  conversationId: number | string | null
  character: Character
  persona: Persona
  messages: StoreMessage[]
  currentBot: Bot
  isCancelled?: () => boolean
}

const ERROR_MESSAGES = {
  SEND_FAILED: 'Failed to send message',
  REROLL_FAILED: 'Failed to reroll message',
}

const findPrecedingUserMessage = (messages: StoreMessage[], aiMessageId: number | string, logger?: Console): string => {
  const aiMessageIndex = messages.findIndex((msg) => msg.id === aiMessageId)

  if (aiMessageIndex === -1) {
    logger?.warn?.(`[RoleplayMessageOrchestrator] Could not find AI message with ID: ${aiMessageId}`)
    return ''
  }

  for (let i = aiMessageIndex - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (!message) continue
    if (message.is_user_author) {
      return message.content
    }
  }

  logger?.warn?.(
    `[RoleplayMessageOrchestrator] No preceding user message found for AI message: ${aiMessageId}`,
  )
  return ''
}

export class RoleplayMessageOrchestrator {
  conversationsStore: RoleplayConversationSessionContract | null | undefined
  messageStore: RoleplayConversationSessionContract
  onMessageUpdate: RoleplayMessageOrchestratorOptions['onMessageUpdate']
  onError: RoleplayMessageOrchestratorOptions['onError']
  api: ApiService
  recipe: ContextRecipe<RoleplayContextInput>
  transport: ConversationTransport
  logger: Console
  abortHandle: StreamHandle | null

  constructor({
    conversationsStore = null,
    messageStore = null,
    onMessageUpdate,
    onError,
    api = apiService,
    recipe = roleplayPrimaryRecipe,
    transport = new ConversationTransport(),
    logger = console,
  }: RoleplayMessageOrchestratorOptions) {
    this.conversationsStore = conversationsStore
    this.messageStore = (messageStore || conversationsStore) as RoleplayConversationSessionContract
    this.onMessageUpdate = onMessageUpdate
    this.onError = onError
    this.api = api
    this.recipe = recipe
    this.transport = transport
    this.logger = logger
    this.abortHandle = null
  }

  stopStreaming(): void {
    this.abortHandle?.abort?.()
    this.abortHandle = null
  }

  /**
   * The roleplay timeline binding: how a running stream reaches the slot store.
   * Both send and reroll use it, so a capability added to generation can never
   * land on one path and skip the other (the drift that hid reasoning on
   * rerolls). The two paths differ only in the target they pass — send streams
   * into a fresh slot, reroll into a new variant of an existing slot.
   *
   * `surfaceErrors` decides what a failed generation leaves behind. A send has
   * nothing else to show, so the failure takes the reply's place. A reroll still
   * has the variant it was replacing, so it reverts instead of stranding the
   * reader on a dead alternative.
   */
  private buildTimelineBinding({ surfaceErrors = false }: { surfaceErrors?: boolean } = {}): TimelineBinding {
    const write = (ctx: TurnStreamContext, updates: MessageUpdate): void => {
      this.messageStore.updateVariant?.(ctx.target.slotId, ctx.target.variantId, updates)
    }

    return {
      begin: (ctx) => {
        const origin = String(ctx.target.slotId) === String(ctx.target.variantId) ? 'message' : 'alternative'
        write(ctx, { ...startResponse(ctx.target.variantId, origin), stream_id: ctx.streamId })
      },
      connected: (ctx) => {
        write(ctx, { phase: 'thinking' })
        this.onMessageUpdate?.(ctx.target.variantId, { phase: 'thinking' })
      },
      reasoning: (ctx, reasoning) => {
        write(ctx, responseProgress('reasoning', reasoning))
      },
      content: (ctx, chunk, content) => {
        write(ctx, responseProgress('content', content))
        this.onMessageUpdate?.(ctx.target.variantId, { content, phase: 'streaming', chunk })
      },
      complete: (ctx, { content }) => {
        write(ctx, {
          ...completeResponse(content),
          stream_id: ctx.streamId,
        })
      },
      remove: (ctx) => {
        // A slot-scoped stream owns its whole slot; a reroll owns only its
        // variant, so the parent slot must survive an errored alternative.
        if (String(ctx.target.slotId) === String(ctx.target.variantId)) {
          this.messageStore.removeMessageLocal(ctx.target.slotId)
        } else {
          this.messageStore.removeVariant?.(ctx.target.slotId, ctx.target.variantId)
        }
      },
      ...(surfaceErrors
        ? {
            failed: (ctx, error) => {
              // In-memory only: the failure is never persisted, so it is gone on
              // reload. The message row renders `phase: 'error'` itself.
              write(ctx, {
                phase: 'error',
                content: parseErrorMessage(error),
                reasoning_content: null,
              })
            },
          }
        : {}),
    }
  }

  async sendMessage({
    messageContent,
    directorNote = null,
    conversationId,
    character,
    persona,
    messages,
    currentBot,
    skipUserMessage = false,
  options = {},
    isCancelled = () => false,
  }: SendMessageParams): Promise<void> {
    if (!messageContent.trim()) return

    const renderedMessageContent = renderConversationMacros(messageContent, { persona })

    // Snapshot the pre-existing assistant slots that this reply will commit.
    // Their alternatives stay visible until the user row is durable, so an
    // insert failure can roll the optimistic turn back without losing choice.
    const assistantSlotsToCommit = !skipUserMessage
      ? messages.filter((message) => !message.is_user_author).map((message) => message.id)
      : []

    // Deterministic ids so the optimistic slots and the stream target coincide
    // (the stream computes `temp-${streamId}` for its message id).
    const streamId = this.transport.generateStreamId()
    const tempAiId = `temp-${streamId}`
    const tempUserId = `temp-user-${streamId}`
    const nowIso = new Date().toISOString()

    // --- Optimistic slots: both land on the same frame the composer clears, so
    // the send feels immediate and symmetric instead of waiting on the network. ---
    if (!skipUserMessage) {
      this.messageStore.addMessage({
        id: tempUserId,
        conversation_id: Number(conversationId),
        content: renderedMessageContent,
        is_user_author: true,
        phase: 'complete',
        stream_id: null,
        created_at: nowIso,
        updated_at: nowIso,
        t: nowIso,
        ...(directorNote ? { director_note: directorNote } : {}),
      } as unknown as StoreMessage)
    }
    this.messageStore.addMessage({
      id: tempAiId,
      conversation_id: Number(conversationId),
      content: '',
      is_user_author: false,
      phase: 'thinking',
      stream_id: streamId,
      created_at: nowIso,
      updated_at: nowIso,
      t: nowIso,
    })

    // --- Persist the user message first (sequential). This guarantees the user
    // row's serial id precedes the assistant's, and lets a failed insert roll
    // back cleanly *before* any tokens are spent. Runs as the turn's prepare
    // phase, so a failure is reported as `prepare` and the runner drops the
    // assistant placeholder. Disposal may land while this write is in flight:
    // the write settles, but the runner's post-prepare cancellation check stops
    // the retired operation before it builds context or starts a stream. ---
    const persistUserMessage = async (): Promise<void> => {
      if (skipUserMessage) return
      try {
        await this.messageStore.persistUserMessage?.(tempUserId, {
          conversation_id: conversationId,
          content: renderedMessageContent,
          director_note: directorNote,
          originalText: messageContent,
        })
      } catch (err: unknown) {
        // An acknowledged rejection rolls the user entry back for composer
        // restoration. Uncertain writes keep their recovery entry instead.
        if (!(err instanceof UserMessageSaveUncertain)) this.messageStore.removeMessageLocal(tempUserId)
        throw err
      }

      // The user's reply is now durable. Commit the visible variant of every
      // pre-existing assistant slot before context is projected for generation.
      // Same-session cancellation still reaches this commit because the user row
      // is already durable.
      if (this.messageStore.flattenToActiveVariant) {
        for (const messageId of assistantSlotsToCommit) {
          this.messageStore.flattenToActiveVariant(messageId)
        }
      }
    }

    let generated: { content: string } | null = null
    try {
      await runStoryTurn({
        prepare: persistUserMessage,
        generate: () => runGenerationTurn({
          // Stream into the pre-created placeholder: fixed slot/variant target.
          target: { slotId: tempAiId, variantId: tempAiId },
          streamId,
          binding: this.buildTimelineBinding({ surfaceErrors: true }),
          persistence: { persistAssistant: async (_ctx, result) => { generated = result } },
          recipe: this.recipe,
          // Read after `prepare`, so the freshly persisted user message is part
          // of the transcript the model sees.
          buildInput: () => ({
            userMessage: renderedMessageContent,
            character,
            persona,
            messages: skipUserMessage ? messages : this.messageStore.messages,
            currentBot,
            options,
          }),
          request: {
            botId: currentBot.id,
            maxTokens: currentBot.max_tokens,
            conversationId,
          },
          isCancelled,
          onStreamHandle: (handle) => { this.abortHandle = handle as StreamHandle },
          hooks: {
            finalize: () => { this.abortHandle = null },
          },
        }, { transport: this.transport }),
        publish: async (outcome) => {
          if (generated) await this.messageStore.persistStreamedMessage?.(tempAiId, {
            conversation_id: conversationId, content: generated.content, stream_id: streamId,
          })
          if (outcome.error) this.onError?.(outcome.error)
        },
      })
    } catch (err: unknown) {
      // The view restores only acknowledged user-save rejection. An uncertain
      // prepare keeps the user bubble, and a failed publish keeps the AI bubble.
      const phase: MessageSendPhase = err instanceof StoryTurnError && err.phase === 'prepare'
        ? err.cause instanceof UserMessageSaveUncertain ? 'user-save-uncertain' : 'user-persist'
        : err instanceof StoryTurnError && err.phase === 'publish' ? 'assistant-persist' : 'stream'
      if (phase === 'user-persist' || phase === 'user-save-uncertain') this.messageStore.removeMessageLocal(tempAiId)
      const sendError = new MessageSendError(
        phase,
        (err as Error)?.message || ERROR_MESSAGES.SEND_FAILED,
      )
      // A failed user insert never reached the stream, so there is nothing to
      // report to the stream-error channel.
      if (phase !== 'user-persist' && phase !== 'user-save-uncertain') this.onError?.(sendError)
      throw sendError
    }
  }

  async rerollMessage({ messageId, conversationId, character, persona, messages, currentBot, isCancelled = () => false }: RerollMessageParams): Promise<void> {
    // Create the alternative variant in memory only; it becomes a real
    // child `messages` row on completion (client-owned persistence). The
    // relay just streams — a reroll is an ordinary generation with reroll
    // context, so no `/v1/reroll` and no server-created placeholder.
    const tempVariantId = this.conversationsStore!.beginReroll(messageId)
    if (tempVariantId == null) {
      throw new Error('Reroll target slot not found.')
    }

    const rerollStreamId = this.transport.generateStreamId()

    let generated: { content: string } | null = null
    try {
      await runStoryTurn({
        prepare: async () => {},
        generate: () => runGenerationTurn({
          // Reroll streams into an existing slot's new variant: the target is
          // fixed (parent slot + the freshly-created temp variant).
          target: { slotId: messageId, variantId: tempVariantId },
          streamId: rerollStreamId,
          binding: this.buildTimelineBinding(),
          persistence: { persistAssistant: async (_ctx, result) => { generated = result } },
          recipe: this.recipe,
          buildInput: () => ({
            userMessage: findPrecedingUserMessage(messages, messageId, this.logger),
            character,
            persona,
            messages,
            currentBot,
            options: {
              isReroll: true,
              excludeMessageId: messageId,
            },
          }),
          request: {
            botId: currentBot.id,
            maxTokens: currentBot.max_tokens,
            conversationId,
          },
          isCancelled,
          onStreamHandle: (handle) => { this.abortHandle = handle as StreamHandle },
          hooks: {
            finalize: () => { this.abortHandle = null },
          },
        }, { transport: this.transport }),
        publish: async (outcome) => {
          if (generated) await this.conversationsStore!.persistStreamedAlternative(messageId, tempVariantId, {
            content: generated.content, stream_id: rerollStreamId,
          })
          if (outcome.error) this.onError?.(outcome.error)
        },
      })
    } catch (err: unknown) {
      if (!(err instanceof StoryTurnError && err.phase === 'publish')) this.conversationsStore!.removeVariant(messageId, tempVariantId)
      this.onError?.(err as Error)
      throw new Error((err as Error).message || ERROR_MESSAGES.REROLL_FAILED)
    }
  }

}

export const createRoleplayMessageOrchestrator = (
  options: RoleplayMessageOrchestratorOptions,
): RoleplayMessageOrchestrator => new RoleplayMessageOrchestrator(options)
